import React, { useState, useEffect } from "react";
import { X, Calendar, Clock, Users } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import api from "../services/api";
import { Employee } from "../types/employee";

interface TimesheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    date: string;
    clockInTime: string;
    clockOutTime: string;
    breakTime: string;
    employeeId: string;
  }) => void;
  editData?: {
    date: string;
    clockInTime: string;
    clockOutTime: string;
    breakTime: string;
    employeeId?: string;
  } | null;
  defaultEmployeeId?: string;
  isSaving?: boolean;
}

const TimesheetModal: React.FC<TimesheetModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editData,
  defaultEmployeeId,
  isSaving = false,
}) => {
  const [employeeId, setEmployeeId] = useState("");
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState<string | null>(null);
  const [date, setDate] = useState("");
  const [clockInTime, setClockInTime] = useState("");
  const [clockOutTime, setClockOutTime] = useState("");
  const [breakTime, setBreakTime] = useState("");

  // Purple theme colors matching the application design
  const primaryPurple = "#885ABB";
  const darkPurple = "#402B69";
  const lightPurple = "#C4A6E1";
  const veryLightPurple = "#F1ECFB";
  const lightPurpleBg = "#F8F5FF";

  useEffect(() => {
    if (editData) {
      setEmployeeId(editData.employeeId || "");
      setDate(editData.date || "");
      setClockInTime(editData.clockInTime || "");
      setClockOutTime(editData.clockOutTime || "");
      setBreakTime(editData.breakTime || "");
    } else {
      setEmployeeId(defaultEmployeeId || "");
      // Set default date to today
      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];
      setDate(formattedDate);
      setClockInTime("");
      setClockOutTime("");
      setBreakTime("");
    }
  }, [editData, isOpen, defaultEmployeeId]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchEmployees = async () => {
      setEmployeesLoading(true);
      setEmployeesError(null);
      try {
        const response = await api.get("/admin/employees");
        const data = response.data;
        let list: Employee[] = [];

        if (Array.isArray(data?.employees)) {
          list = data.employees;
        } else if (data?.success && data?.data) {
          if (Array.isArray(data.data?.data)) {
            list = data.data.data;
          } else if (Array.isArray(data.data)) {
            list = data.data;
          }
        }

        setEmployees(list);

        if (!editData && !employeeId && list.length > 0) {
          setEmployeeId(String(list[0].id));
        }
      } catch (error) {
        console.error("Failed to fetch employees for timesheet modal:", error);
        setEmployees([]);
        setEmployeesError("Failed to load employees.");
      } finally {
        setEmployeesLoading(false);
      }
    };

    fetchEmployees();
  }, [isOpen, editData, employeeId]);

  const handleSave = () => {
    if (!employeeId) {
      alert("Please select an employee");
      return;
    }

    if (!date || !clockInTime || !clockOutTime) {
      alert("Please fill in all required fields (Employee, Date, Clock-in Time, Clock-out Time)");
      return;
    }

    onSave({
      employeeId,
      date,
      clockInTime,
      clockOutTime,
      breakTime: breakTime || "0",
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 border"
        style={{ borderColor: veryLightPurple }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">
            {editData ? "Edit Timesheet" : "Create Timesheet"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-6">
          {/* Employee Field */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: primaryPurple }}
            >
              Employee<span className="text-red-500 ml-1">*</span>
            </label>
            <Select
              value={employeeId}
              onValueChange={setEmployeeId}
              disabled={employeesLoading || employees.length === 0}
            >
              <SelectTrigger className="w-full h-11 border rounded-md text-sm bg-white disabled:opacity-60">
                <SelectValue
                  placeholder={
                    employeesLoading
                      ? "Loading employees..."
                      : employees.length === 0
                      ? "No employees available"
                      : "Select employee"
                  }
                />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-lg max-h-60 overflow-auto z-[150]">
                {employees.length === 0 ? (
                  <SelectItem value="no-employees" disabled>
                    <div className="flex items-center gap-2 text-gray-500">
                      <Users className="w-4 h-4" />
                      <span>No employees available</span>
                    </div>
                  </SelectItem>
                ) : (
                  employees.map((emp) => (
                    <SelectItem key={emp.id} value={String(emp.id)}>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-500" />
                        <span>{emp.name}</span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {employeesError && (
              <p className="mt-1 text-xs text-red-500">{employeesError}</p>
            )}
          </div>

          {/* Date Field */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: primaryPurple }}
            >
              Date<span className="text-red-500 ml-1">*</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                id="timesheetDate"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="flex-1 px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 transition-colors"
                style={{
                  borderColor: veryLightPurple,
                  backgroundColor: veryLightPurple,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = primaryPurple;
                  e.target.style.boxShadow = `0 0 0 2px ${veryLightPurple}`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = veryLightPurple;
                  e.target.style.boxShadow = "none";
                }}
                required
              />
              <button
                type="button"
                className="w-10 h-10 flex items-center justify-center rounded-md transition-colors hover:opacity-90"
                style={{
                  backgroundColor: primaryPurple,
                  color: "white",
                }}
                onClick={() => {
                  const dateInput = document.getElementById("timesheetDate") as HTMLInputElement | null;
                  dateInput?.showPicker?.();
                }}
              >
                <Calendar className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Clock-in Time Field */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: primaryPurple }}
            >
              Clock-in Time<span className="text-red-500 ml-1">*</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="time"
                id="clockInTime"
                value={clockInTime}
                onChange={(e) => setClockInTime(e.target.value)}
                className="flex-1 px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 transition-colors"
                style={{
                  borderColor: veryLightPurple,
                  backgroundColor: veryLightPurple,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = primaryPurple;
                  e.target.style.boxShadow = `0 0 0 2px ${veryLightPurple}`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = veryLightPurple;
                  e.target.style.boxShadow = "none";
                }}
                required
              />
              <button
                type="button"
                className="w-10 h-10 flex items-center justify-center rounded-md transition-colors hover:opacity-90"
                style={{
                  backgroundColor: primaryPurple,
                  color: "white",
                }}
                onClick={() => {
                  const timeInput = document.getElementById("clockInTime") as HTMLInputElement;
                  timeInput?.showPicker?.();
                  timeInput?.focus();
                }}
              >
                <Clock className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Clock-out Time Field */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: primaryPurple }}
            >
              Clock-out Time<span className="text-red-500 ml-1">*</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="time"
                id="clockOutTime"
                value={clockOutTime}
                onChange={(e) => setClockOutTime(e.target.value)}
                className="flex-1 px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 transition-colors"
                style={{
                  borderColor: veryLightPurple,
                  backgroundColor: veryLightPurple,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = primaryPurple;
                  e.target.style.boxShadow = `0 0 0 2px ${veryLightPurple}`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = veryLightPurple;
                  e.target.style.boxShadow = "none";
                }}
                required
              />
              <button
                type="button"
                className="w-10 h-10 flex items-center justify-center rounded-md transition-colors hover:opacity-90"
                style={{
                  backgroundColor: primaryPurple,
                  color: "white",
                }}
                onClick={() => {
                  const timeInput = document.getElementById("clockOutTime") as HTMLInputElement;
                  timeInput?.showPicker?.();
                  timeInput?.focus();
                }}
              >
                <Clock className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Break Time Field */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: primaryPurple }}
            >
              Break Time (in mins)
            </label>
            <input
              type="number"
              value={breakTime}
              onChange={(e) => setBreakTime(e.target.value)}
              placeholder="e.g. 30"
              min="0"
              className="w-full px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 transition-colors"
              style={{
                borderColor: veryLightPurple,
                backgroundColor: veryLightPurple,
              }}
              onFocus={(e) => {
                e.target.style.borderColor = primaryPurple;
                e.target.style.boxShadow = `0 0 0 2px ${veryLightPurple}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = veryLightPurple;
                e.target.style.boxShadow = "none";
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2.5 rounded-lg text-sm font-medium text-white transition-colors hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: primaryPurple }}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimesheetModal;
